import { ProfileFilter } from "@happy-legal/graphql-types";
import {
  Box,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { ChangeEvent, Dispatch, SetStateAction } from "react";

type Audience = "all" | "subscribers" | "nonSubscribers";

const AUDIENCES: { value: Audience; label: string }[] = [
  { value: "all", label: "Alle" },
  { value: "subscribers", label: "Nur Abonnenten" },
  { value: "nonSubscribers", label: "Nur Nicht-Abonnenten" },
];

// Recipient filter
type NewsletterFilter = {
  filter: ProfileFilter;
  setFilter: Dispatch<SetStateAction<ProfileFilter>>;
  notEqual: ProfileFilter;
  setNotEqual: Dispatch<SetStateAction<ProfileFilter>>;
};

/**
 * Chooses who receives the newsletter.
 *
 * The audience was two checkboxes that each disabled the other - a
 * three-way choice wearing the clothes of two independent ones. It is
 * a single exclusive control now; the filter shape it produces is
 * unchanged.
 */
const NewsletterFilter = ({
  filter,
  setFilter,
  notEqual,
  setNotEqual,
}: NewsletterFilter) => {
  const audience: Audience =
    notEqual?.abos === null
      ? "subscribers"
      : filter?.abos === null
        ? "nonSubscribers"
        : "all";

  const handleAudience = (_: unknown, next: Audience | null) => {
    // ToggleButtonGroup hands back null when the active button is
    // clicked again; keep the current choice rather than clearing it.
    if (!next) return;

    setNotEqual((next === "subscribers" ? { abos: null } : {}) as ProfileFilter);

    setFilter((prevFilter) => {
      if (next === "nonSubscribers") {
        return { ...prevFilter, abos: null } as ProfileFilter;
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { abos = null, ...rest } = prevFilter;
      return rest as ProfileFilter;
    });
  };

  const handleCityFilter = (event: ChangeEvent<HTMLInputElement>) => {
    const city = event.target.value;

    setFilter((prevFilter) => {
      if (city) {
        return {
          ...prevFilter,
          rentalObjects: [{ address: { city } }],
        } as ProfileFilter;
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { rentalObjects = [], ...newFilter } = prevFilter;
      return newFilter;
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-end",
        gap: 2,
        flexWrap: "wrap",
      }}
    >
      <Box>
        <Typography
          variant="overline"
          component="div"
          sx={{ color: "text.secondary", mb: 0.5 }}
        >
          Empfänger
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={audience}
          onChange={handleAudience}
          aria-label="Empfängerkreis"
        >
          {AUDIENCES.map(({ value, label }) => (
            <ToggleButton key={value} value={value} sx={{ px: 1.5 }}>
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <TextField
        size="small"
        variant="outlined"
        label="Stadt"
        placeholder="Alle Städte"
        onChange={handleCityFilter}
        value={filter?.rentalObjects?.[0]?.address?.city ?? ""}
        InputLabelProps={{ shrink: true }}
        sx={{ width: 180 }}
      />
    </Box>
  );
};

export default NewsletterFilter;
